import React, { useContext, useEffect, useMemo, useState } from "react";
import { Dialog, GridGenerator } from "@actinc/dls/components";
import { AlertContext } from "@actinc/dls/context";
import {
  Magnify as MagnifyIcon,
  OpenInNew as OpenInNewIcon,
} from "@actinc/dls/icons";
import _ from "lodash/fp";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  Paper,
  Tab,
  Typography,
} from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useClient } from "urql";

import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { PreviewContentDialog } from "../../Dashboard/Tasks/PreviewContentDialog";
import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";

import {
  AlertDialog,
  ControlledFormInput,
  ControlledFormSelect,
  DataGrid,
  ExpandMoreIcon,
  IconToolbar,
  PreviewContent,
  StandardsDialog,
} from "../../../components";

import {
  useGetMetadataConfigurationOptions,
  useStore,
} from "../../../services/hooks";
import {
  clearState,
  getState,
  removeState,
  setState,
} from "../../../services/sessionState";
import {
  filterOutSelectedPrograms,
  getOptionsByProgram,
  getSelectedItemOptionsFromMetadata,
  getTaskOptions,
  getUserOptions,
  getWorkspaceURIFromTask,
  PREVIEW_TYPES,
  setTitle,
  stringSortComparator,
} from "../../../services/utils";

const SearchTasks = () => {
  const client = useClient();
  const datagridKey = "taskSearch";
  const alertContext = useContext(AlertContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);

  const { control, getValues, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues,
  });
  const { isSubmitting } = useFormState({ control });
  const selectedProgram = watch("program");

  const location = useLocation();
  setState("lastSearchLocation")(location.pathname);
  const navigate = useNavigate();

  const [checkedGridRows, setCheckedGridRows] = useState([]);
  const [dialogState, setDialogState] = useState({
    dialogName: "",
    open: false,
  });
  const [closeTasksResponse, closeTasks] = useMutation(closeTasksMutation);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Metadata");
  const [standardsDialogProps, setStandardsDialogProps] = useState({
    open: false,
  });

  const [search, searchStore] = useStore("search");

  const [previewContentDialogProps, setPreviewContentDialogProps] = useState({
    open: false,
  });

  const [
    contentArea,
    developmentState,
    format,
    gradeLevel,
    type,
    elementStatus,
    language,
    release,
    restriction,
    unavailability,
  ] = useGetMetadataConfigurationOptions(
    metadataConfiguration,
    programs,
    "item",
    [
      "contentArea",
      "developmentState",
      "format",
      "gradeLevel",
      "type",
      "elementStatus",
      "language",
      "release",
      "restriction",
      "unavailability",
    ],
  );

  // Set the document title
  useEffect(() => {
    setTitle(["CDE", "Task Search"]);
  }, []);

  useEffect(() => {
    if (!searchStore.client) searchStore.init(client);
  }, [client]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!loading) {
      if (
        (!location.state?.from ||
          [location.pathname, "/workspace"].includes(location.state?.from)) &&
        search?.type === "task"
      ) {
        Object.keys(search.params).map((key) =>
          setValue(key, search.params[key]),
        );
      } else if (!closeTasksResponse?.data) {
        if (!_.isEmpty(search)) {
          searchStore.set({});
        } else if (getState("taskSearchFilters")) {
          const taskSearchFilters = getState("taskSearchFilters");
          changeTab(null, "Metadata");
          Object.keys(taskSearchFilters).map((key) =>
            setValue(key, taskSearchFilters[key]),
          );
          onMetadataSearchSubmit();
          clearState();
        }
      }
    }
    setLoading(false);
  }, [search]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const permittedActions = useMemo(
    () =>
      checkedGridRows
        .map((row) => row._actions)
        .reduce((acc, actions) => _.intersection(acc)(actions), ["closeTasks"]),
    [checkedGridRows],
  );

  const programOptions = useMemo(
    () =>
      (programs &&
        programs
          .filter(({ _actions }) => _actions.includes("searchContent"))
          .map(({ id, name }) => ({ label: name, value: id }))) ||
      [],
    [programs],
  );

  const taskNameOptions = useMemo(() => getTaskOptions(), []);

  const selectedItemProgramIds = checkedGridRows.map((item) => item?.program);

  const getFieldOptions = useMemo(() => {
    return getSelectedItemOptionsFromMetadata(programs)(selectedItemProgramIds)(
      {
        developmentState,
        elementStatus,
        language,
        release,
        restriction,
        unavailability,
      },
    );
  }, [
    developmentState,
    elementStatus,
    language,
    programs,
    release,
    restriction,
    selectedItemProgramIds,
    unavailability,
  ]);

  const rows =
    (search?.response?.data && search?.response?.data.tasksByMetadata) || [];

  const handleSelectedIds = useMemo(() => {
    const allCheckedStatesMatch = checkedGridRows.every((row) =>
      ["Peer Review 1", "Peer Review 2", "Peer Review 3"].includes(
        row.developmentState,
      ),
    );

    return rows
      .filter((row) => {
        const developmentStates = [
          "Peer Review 1",
          "Peer Review 2",
          "Peer Review 3",
        ];
        return allCheckedStatesMatch
          ? checkedGridRows.every(
              (task) =>
                task?.assignedUser === row.assignedUser &&
                developmentStates.some((state) =>
                  row?.developmentState?.includes(state),
                ),
            )
          : developmentStates.every(
              (state) => !row?.developmentState?.includes(state),
            );
      })
      .map((row) => row.id);
  }, [checkedGridRows, rows]);

  const developmentStateOptions = useMemo(() => {
    const devStateValues = [...new Set(rows.map((row) => row.developmentState))]
      .filter(Boolean)
      .sort();
    return devStateValues;
  }, [rows]);

  const taskNameValueOptions = useMemo(() => {
    const taskValues = [...new Set(rows.map((row) => row.name))]
      .filter(Boolean)
      .sort();
    return taskValues;
  }, [rows]);

  if (!programOptions.length) return null;

  const changeTab = (_e, tab) => {
    setSelectedTab(tab);
    reset({ ...defaultValues });
    searchStore.set({});
  };

  const closeDialog = () => {
    setDialogState({ open: false, dialogName: "" });
  };

  const getOriginalQuery = (string) => {
    switch (string) {
      case "searchTasksByMetadata":
        return searchTasksByMetadataQuery;
      default:
        break;
    }
  };

  const handleSelectionModelChange = (data) => (selection) => {
    const selectedRows = data.filter(({ id }) => selection.includes(id));
    setCheckedGridRows(selectedRows);
  };

  const handleRefetch = async () => {
    const originalQueryName =
      search?.response?.operation?.query?.definitions[0]?.name?.value;
    setLoading(true);
    await searchStore.fetch("task")(getOriginalQuery(originalQueryName))({
      ...search?.params,
      requestPolicy: "network-only",
    });
  };

  const onMetadataSearchSubmit = async () => {
    const searchValues = getValues();
    setLoading(true);
    await searchStore.fetch("task")(searchTasksByMetadataQuery)({
      ...searchValues,
      itemName: searchValues?.itemName.trim(),
      passageName: searchValues?.passageName.trim(),
      rowLimit: !searchValues.rowLimit ? null : searchValues.rowLimit,
    });
  };

  const openItem = (newWindow) => (row) => () => {
    const uri = getWorkspaceURIFromTask(row);
    if (newWindow) {
      window.open(uri, row.workspace.name);
    } else {
      navigate(uri, { reloadDocument: true });
    }
  };

  const getTarget = (task) => {
    const name =
      task.passage?.name || task.item?.passage?.name || task.item?.name;
    return name;
  };

  const openPreviewContentDialog = (object) => (_e) => {
    setPreviewContentDialogProps({
      onClose: () => setPreviewContentDialogProps({ open: false }),
      open: true,
      object,
    });
  };

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Task Name",
      renderCell: (params) => {
        const passageOnly =
          !params?.row?.item?.id && !!params?.row?.passage?.id;
        const uri = getWorkspaceURIFromTask(params.row);
        return (
          <>
            <Link
              component={RouterLink}
              reloadDocument
              to={uri}
              underline="always"
            >
              {params.value}
            </Link>
            <IconButton
              color="secondary"
              component={RouterLink}
              size="small"
              target={getTarget(params?.row)}
              to={uri}
            >
              <OpenInNewIcon />
            </IconButton>
            <PreviewContent
              objects={
                passageOnly
                  ? [
                      {
                        id: params?.row?.passage?.id,
                        versionID: params?.row?.passage?.versionID,
                      },
                    ]
                  : [
                      {
                        id: params?.row?.item?.id,
                        versionID: params?.row?.item?.versionID,
                      },
                    ]
              }
              objectType={passageOnly ? "Passage" : "Item"}
              previewTypes={PREVIEW_TYPES}
            />
          </>
        );
      },
      sortComparator: stringSortComparator,
      type: "singleSelect",
      valueOptions: taskNameValueOptions,
    },
    {
      field: "developmentState",
      headerName: "Development State",
      minWidth: 200,
      sortComparator: stringSortComparator,
      type: "singleSelect",
      valueGetter: ({ row }) => row.developmentState,
      valueOptions: developmentStateOptions,
    },
    {
      field: "assignee",
      headerName: "Assignee",
      minWidth: 200,
      sortComparator: stringSortComparator,
      type: "string",
      valueGetter: getAssigneeName(users),
    },
    {
      field: "passageName",
      flex: 1,
      headerName: "Passage Name",
      minWidth: 150,
      sortComparator: stringSortComparator,
      type: "string",
      valueGetter: ({ row }) =>
        row.passage?.name || row.item?.passage?.name || "",
    },
    {
      field: "itemName",
      flex: 1,
      headerName: "Item Name",
      sortComparator: stringSortComparator,
      type: "string",
      valueGetter: ({ row }) => row.item?.name || "",
    },
    {
      field: "contentSpecialist",
      headerName: "Content Specialist",
      minWidth: 200,
      sortComparator: stringSortComparator,
      type: "string",
      valueGetter: getContentSpecialistName(users),
    },
  ];

  const handleCloseTasks = (e) => {
    setCheckedGridRows([]);
    closeTasksProcess(rows)(checkedGridRows);
  };

  const closeTasksProcess =
    (tasks = []) =>
    async (selection = []) => {
      setLoading(true);
      let updatedTasks = [...tasks];
      for (const task of selection) {
        const taskID = task.id;
        const { developmentState, taskDefinition } =
          task.taskDefinition.completion.options[0];
        const response = await closeTasks({
          taskIDs: [taskID],
          developmentState,
          taskDefinition: developmentState ? null : taskDefinition.key,
        });
        if (response.data?.closeTasks) {
          updatedTasks = updatedTasks.filter((t) => t.id !== taskID);
        }
      }

      searchStore.set({
        response: {
          data: {
            tasksByMetadata: updatedTasks,
          },
        },
      });

      setLoading(false);
    };

  const dataGridProps = {
    actions: [
      {
        label: "Open",
        onClick: openItem(false),
      },
      {
        label: "Open in New Window",
        onClick: openItem(true),
      },
      {
        label: "Preview",
        onClick: (row) =>
          openPreviewContentDialog(
            !row?.item?.id && !!row?.passage?.id
              ? {
                  id: row?.passage?.id,
                  type: "Passage",
                  versionID: row?.passage?.versionID,
                  permissions: row.taskDefinition?.execution?.permissions || [],
                }
              : {
                  id: row?.item?.id,
                  type: "Item",
                  versionID: row?.item?.versionID,
                  permissions: row.taskDefinition?.execution?.permissions || [],
                },
          ),
      },
    ],
    checkboxSelection: rows
      .map((row) => row?.[`_actions`])
      .flat()
      .some((action) => ["closeTasks"].includes(action)),
    columns,
    disableRowSelectionOnClick: true,
    disableSelectAllCheckbox: true,
    isRowSelectable: ({ row }) => {
      return (
        handleSelectedIds?.includes(row.id) &&
        row?._actions?.some((action) => ["closeTasks"].includes(action))
      );
    },
    getRowClassName: ({ row }) => (row.ready ? "ready" : ""),
    loading,
    onRowSelectionModelChange: handleSelectionModelChange(rows),
    rows,
    datagridKey,
    sortModel: [
      {
        field: "assignedDateTime",
        sort: "asc",
      },
    ],
  };
  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "xs",
    onClose: closeDialog,
    open: dialogState.open && dialogState.dialogName === "listSearch",
    scroll: "paper",
    title: "Items Not Found",
  };

  const rowLimitOptions = [100, 250, 500].map((value) => ({
    label: value,
    value,
  }));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">Tasks</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <TabContext value={selectedTab}>
          <TabList onChange={changeTab}>
            <Tab key="Metadata" label="Metadata" value="Metadata" />
          </TabList>
          <TabPanel
            key="Metadata"
            index="0"
            sx={{ overflowY: "auto" }}
            value="Metadata"
          >
            <Paper
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: 2,
                padding: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSubmit(onMetadataSearchSubmit)}>
                  <Box
                    sx={{
                      alignItems: "end",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ marginBottom: 2 }}>
                      <ExpandMoreIcon
                        expand={expanded}
                        onClick={() => setExpanded(!expanded)}
                      />
                    </Box>
                    <Collapse
                      aria-label={`collapsable panel ${
                        expanded ? "expanded" : "collapsed"
                      }`}
                      in={expanded}
                      sx={{ width: "100%" }}
                      timeout="auto"
                    >
                      <GridGenerator
                        groups={[
                          [
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  fullWidth={true}
                                  id={`taskSearch-program`}
                                  label="Program"
                                  name="program"
                                  options={programOptions}
                                  rules={{
                                    required: true,
                                  }}
                                />
                              ),
                              key: `taskSearch-program`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`taskSearch-taskName`}
                                  label="Task Name"
                                  name="taskName"
                                  options={taskNameOptions}
                                />
                              ),
                              key: `taskSearch-taskName`,
                            },
                            {
                              children: (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <ControlledFormSelect
                                    control={control}
                                    id={`taskSearch-rowLimit`}
                                    label="Number of Results"
                                    name="rowLimit"
                                    options={rowLimitOptions}
                                  />
                                </Box>
                              ),
                              key: `taskSearch-rowLimit`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`taskSearch-assignee`}
                                  label="Assignee"
                                  name="assignee"
                                  options={getUserOptions(users)({
                                    activeOnly: false,
                                    programs: [selectedProgram],
                                  })}
                                />
                              ),
                              key: `taskSearch-assignee`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  fullWidth={true}
                                  disabled={!selectedProgram}
                                  id={`taskSearch-developmentState`}
                                  label="Development State"
                                  name="developmentState"
                                  options={getOptionsByProgram(programs)([
                                    selectedProgram,
                                  ])(developmentState)}
                                />
                              ),
                              key: `taskSearch-developmentState`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`taskSearch-taskState`}
                                  label="Open|Closed"
                                  name="taskState"
                                  options={[
                                    { label: "Open", value: "Open" },
                                    { label: "Closed", value: "Closed" },
                                  ]}
                                  rules={{
                                    required: true,
                                  }}
                                />
                              ),
                              key: `taskSearch-taskState`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  fullWidth={true}
                                  id={`taskSearch-itemName`}
                                  label="Item Name"
                                  name="itemName"
                                />
                              ),
                              key: `taskSearch-itemName`,
                            },
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  fullWidth={true}
                                  id={`taskSearch-passageName`}
                                  label="Passage Name"
                                  name="passageName"
                                />
                              ),
                              key: `taskSearch-passageName`,
                            },
                          ],
                        ]}
                      />
                    </Collapse>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Button
                        onClick={() => {
                          reset({ ...defaultValues });
                          searchStore.set({});
                          removeState("taskSearchFilters");
                          setCheckedGridRows([]);
                        }}
                        sx={{ marginRight: 2 }}
                        variant="outlined"
                      >
                        Reset
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        sx={{ marginRight: 2 }}
                        type="submit"
                        variant="contained"
                      >
                        Search
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </TabPanel>
        </TabContext>
      </Box>
      {(search?.response || loading) && (
        <>
          <Paper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              margin: 1,
            }}
          >
            {!!checkedGridRows.length && (
              <IconToolbar
                multipleSelected={checkedGridRows.length > 1}
                objectType="Task"
                handleCloseTasks={handleCloseTasks}
                permittedActions={permittedActions}
              ></IconToolbar>
            )}
            <Box
              sx={{
                flexGrow: 1,
                minHeight: "500px",
              }}
            >
              <DataGrid {...dataGridProps}></DataGrid>
            </Box>
          </Paper>
        </>
      )}
      <Dialog {...dialogProps}>
        <DialogContent>
          <Typography variant="subtitle2">
            No results matched the following item
            {!!dialogState?.itemsNotFound &&
            dialogState.itemsNotFound.length > 1
              ? "s"
              : ""}
            :
          </Typography>
          <List dense={true} disablePadding={true}>
            {!!dialogState?.itemsNotFound &&
              dialogState.itemsNotFound.map((itemName) => (
                <ListItem key={itemName}>{itemName}</ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {dialogState.open && dialogState.dialogName === "delete" && (
        <AlertDialog {...dialogState} />
      )}
      {previewContentDialogProps.open && (
        <PreviewContentDialog {...previewContentDialogProps} />
      )}
    </>
  );
};

const searchTasksByMetadataQuery = `
  query searchTasksByMetadataQuery($assignee: String, $developmentState: String, $itemName: String, $passageName: String, $program: ID!, $rowLimit: Int, $taskName: String, $taskState: String) {
    tasksByMetadata(assignee: $assignee, developmentState: $developmentState, itemName: $itemName, passageName: $passageName, program: $program, rowLimit: $rowLimit, taskName: $taskName, taskState: $taskState) {
      _actions
      assignedUser
      developmentState
      id
      item {
        _actions
        contentSpecialist {
          id
          name
        }
        id
        name
        passage{
          name
        }
        versionID
      }
      name
      passage {
        _actions
        contentSpecialist {
          id
          name
        }
        id
        name
        versionID
      }
      taskDefinition {
        completion {
          options {
            buttonLabel
            developmentState
            taskDefinition {
              assignment {
                multiple
                pool
                roles
                scope
              }
              creation {
                details
              }
              key
            }
          }
          prerequisites
        }
        execution {
          application
          permissions
        }
        key
      }
    }
  }
`;

const closeTasksMutation = `
  mutation closeTasks( $developmentState: String, $taskDefinition: String, $taskIDs: [ID!]!) {
    closeTasks( developmentState: $developmentState, taskDefinition: $taskDefinition, taskIDs: $taskIDs) {
      completedDateTime
      completedByUser
      id
    }
  }`;

const defaultValues = {
  assignee: null,
  developmentState: null,
  itemName: "",
  passageName: "",
  program: null,
  rowLimit: 200,
  taskName: null,
  taskState: "Open",
};

const getNonNullValue = (fieldName) => (params) =>
  `${params.row?.[fieldName] || ""}`;
const getPassageName = (params) => `${params.row?.passage?.name || ""}`;
const getProgramName = (programs) => (params) =>
  `${programs.find(({ id }) => id === params.row?.program)?.name || ""}`;
const getContentSpecialistName = (users) => (params) =>
  `${
    users.find(
      ({ id }) =>
        id ===
        (params.row?.item
          ? params.row?.item?.contentSpecialist?.id
          : params.row?.passage?.contentSpecialist?.id),
    )?.name || ""
  }`;

const getAssigneeName = (users) => (params) =>
  `${users.find(({ id }) => id === params.row?.assignedUser)?.name || ""}`;

export {
  SearchTasks,
  searchTasksByMetadataQuery,
  getProgramName,
  closeTasksMutation,
};
